import React from 'react';
import CurrencyFormat from 'react-currency-format';
import { formatCustomDate } from '../../helpers/formatCustomDate';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import { CreditNoteDetails, AccountInfo } from './interface/CreditNote.Interface';
import StatusBadge from '../../generals/StatusBadge';

// Types

interface CreditNoteDetailsContentProps {
  creditNoteDetails: CreditNoteDetails;
  id: string;
  viewCustomerDetails: (customerId: string) => void;
  accountInfo?: AccountInfo[];
}

const CreditNoteDetailsContent = React.forwardRef<HTMLDivElement, CreditNoteDetailsContentProps>((props, ref) => {
  const { creditNoteDetails, id, accountInfo } = props;

  const { totalDebt, totalCredit } = React.useMemo(() => {
    if (!Array.isArray(accountInfo) || accountInfo.length === 0) {
      return { totalDebt: 0, totalCredit: 0 };
    }

    return accountInfo.reduce((acc, item) => ({
      totalDebt: acc.totalDebt + (item.debit_amount || 0),
      totalCredit: acc.totalCredit + (item.credit_amount || 0)
    }), { totalDebt: 0, totalCredit: 0 });
  }, [accountInfo]);

  return (
    <div ref={ref} className="bg-white shadow-lg rounded-lg p-8 mx-auto">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-8">
        <div className="text-left">
          <StatusBadge
            statusId={creditNoteDetails?.status_id}
            statusName={creditNoteDetails?.status_name}
            size="md"
          />
          <p className="text-gray-600 mt-2">Nigeria</p>
        </div>
        <div className="text-right">
          <h2 className="text-4xl font-bold">CREDIT NOTE</h2>
          <p className="text-gray-600"># {creditNoteDetails?.credit_note_number}</p>
        </div>
      </div>

      {/* Credit Info */}
      <div className="flex justify-between mb-6">
        <div className="text-left">
          <p onClick={() => props?.viewCustomerDetails(creditNoteDetails?.customer_id)} className="font-bold text-primary show-cursor">
            Bill To {creditNoteDetails?.customer_full_name}
          </p>
          <p onClick={() => props?.viewCustomerDetails(creditNoteDetails?.customer_id)} className="font-bold text-primary show-cursor">
            Email {creditNoteDetails?.customer_email_address}
          </p>
          <p onClick={() => props?.viewCustomerDetails(creditNoteDetails?.customer_id)} className="font-bold text-primary show-cursor">
            Mobile {creditNoteDetails?.customer_mobile_no}
          </p>
        </div>

        <div className="text-right">
          <p className="text-gray-600">Credit Date: {formatCustomDate(creditNoteDetails?.transaction_date)}</p>
          <p className="text-gray-600">Ref #: {id}</p>
        </div>
      </div>

      {/* Item Description Table */}
      {/* <div className="border-t border-b mb-6">
        <table className="w-full text-left table-auto">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-2">#</th>
              <th className="py-2">Item & Description</th>
              <th className="py-2">Qty</th>
              <th className="py-2">Rate</th>
              <th className="py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            
                {creditNoteDetails?.map((item, index)=> {

                    return (
                        <tr>
                         <td className="py-2">{index+1} </td>
                        <td className="py-2">
                            <strong>{item?.Item_Name} </strong>
                            <br />
                            <span className="text-gray-500 text-sm">Sample collection offlinePayment text</span>
                        </td>
                        <td className="py-2">1</td>
                        <td className="py-2"> {item?.Item_Rate_Market} </td>
                        <td className="py-2">{item?.Item_Rate_Zoho} </td>
                        </tr>

                    )
                })}
             
            
          </tbody>
        </table>
      </div> */}

      {/* Subtotal and Total */}
      <div className="text-right mb-6">
        <p className="text-xl font-bold">
          Amount:{' '}
          <span className="text-gray-600">
            <CurrencyFormat
              value={formatCustomNumber(creditNoteDetails?.amount)}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'₦'}
            />
          </span>
        </p>
        <p className="text-xl font-bold">
          Used Amount:{' '}
          <span className="text-gray-600">
            <CurrencyFormat
              value={formatCustomNumber(creditNoteDetails?.used_amount)}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'₦'}
            />
          </span>
        </p>
        <p className="text-xl font-bold">
          Remaining Amount:{' '}
          <span className="text-gray-600">
            <CurrencyFormat
              value={formatCustomNumber(creditNoteDetails?.remaining_amount)}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'₦'}
            />
          </span>
        </p>
      </div>

       {/* Credits Remaining */}
      <p className="p-3">Remark: {creditNoteDetails?.remark}</p>

      {/* Journal Entry Section */}
      {accountInfo && accountInfo.length > 0 && (
        <div className="bg-white shadow-md rounded-md mx-auto my-8 p-6 border-t border-gray-150">
          <header className="flex justify-between items-center border-b pb-4">
            <h1 className="text-xl font-bold uppercase">Journal</h1>
            <p className="text-sm text-gray-500">#{accountInfo[0]?.reference_number}</p>
            <div className="m-2 p-2">
              <StatusBadge
                statusId={accountInfo[0]?.status_id}
                statusName={accountInfo[0]?.status_name}
                size="md"
              />
            </div>
          </header>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Journal Number:</span> {accountInfo[0]?.journal_number}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-medium">Reference Number:</span> {accountInfo[0]?.reference_number}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-medium">Notes:</span> {accountInfo[0]?.notes}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Date:</span> {formatCustomDate(accountInfo[0]?.encoded_date || accountInfo[0]?.journal_date)}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-medium">Journal Type:</span> {accountInfo[0]?.journal_type_name}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-medium">Transaction Type:</span> {accountInfo[0]?.transation_type_name}
              </p>
            </div>
          </div>

          <table className="w-full mt-6 border-collapse border border-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">No.</th>
                <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">Account</th>
                <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">Remark</th>
                <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 text-right">Debits</th>
                <th className="border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 text-right">Credits</th>
              </tr>
            </thead>
            <tbody>
              {accountInfo.map((item, index) => (
                <tr key={item.account_id || index}>
                  <td className="border border-gray-200 p-2 text-sm text-gray-600 text-right">
                    {item?.account_id}
                  </td>
                  <td className="border border-gray-200 text-sm p-2 text-gray-600 text-left">
                    {item?.account_name}
                  </td>
                  <td className="border border-gray-200 p-2 text-sm text-gray-600">
                    {item?.remark}
                  </td>
                  <td className="border border-gray-200 p-2 text-sm text-gray-600 text-right">
                    {formatCustomNumber(item?.debit_amount)}
                  </td>
                  <td className="border border-gray-200 p-2 text-sm text-gray-600 text-right">
                    {formatCustomNumber(item?.credit_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="border border-gray-200 font-bold text-gray-800">
                  Total
                </td>
                <td className="border border-gray-200 px-4 py-2">-</td>
                <td className="border border-gray-200 px-4 py-2">-</td>
                <td className="border border-gray-200 px-4 py-2 text-right font-bold text-red-600">
                  NGN {formatCustomNumber(totalDebt)}
                </td>
                <td className="border border-gray-200 text-right font-bold text-green-600">
                  NGN {formatCustomNumber(totalCredit)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
});

CreditNoteDetailsContent.displayName = 'CreditNoteDetailsContent';

export default CreditNoteDetailsContent;
